import type { UserItemState } from "./types";

/**
 * Per-server watch-state cache. The server is the source of truth
 * (ARCHITECTURE §4c userState travels on every item payload); this cache is
 * warmed as items load so `getUserState` can stay synchronous, and it emits
 * to subscribers so posters re-render after playback. Optimistic updates on
 * progress/scrobble keep the UI snappy; the next fetch reconciles.
 */

const EMPTY: UserItemState = {
  watched: false,
  viewOffsetMs: 0,
  viewCount: 0,
  lastPlayedAt: null,
  audioStreamIndex: null,
  subtitleStreamIndex: null,
};

export class ServerWatchState {
  private cache = new Map<string, UserItemState>();
  private listeners = new Set<() => void>();
  private version = 0;
  private snap: Record<string, number> = {};
  /**
   * Items whose stream selection has a local write in flight: an ingest of
   * server truth must not clobber it before the PUT lands (select on the
   * detail page → press Play immediately → the player's item fetch races
   * the PUT). Cleared when the write settles.
   */
  private dirtySelection = new Set<string>();

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  /** Stable snapshot for useSyncExternalStore (identity changes on emit). */
  snapshot = (): Record<string, number> => this.snap;

  private emit(): void {
    this.version += 1;
    this.snap = { v: this.version };
    for (const fn of this.listeners) fn();
  }

  get(itemId: string): UserItemState {
    return this.cache.get(itemId) ?? EMPTY;
  }

  /** Ingest server-truth state from an item payload (no emit storm on reads). */
  ingest(itemId: string, state: UserItemState): void {
    const prev = this.cache.get(itemId);
    let next = state;
    if (prev && this.dirtySelection.has(itemId)) {
      next = {
        ...state,
        audioStreamIndex: prev.audioStreamIndex,
        subtitleStreamIndex: prev.subtitleStreamIndex,
      };
    }
    if (
      prev &&
      prev.watched === next.watched &&
      prev.viewOffsetMs === next.viewOffsetMs &&
      prev.viewCount === next.viewCount &&
      prev.audioStreamIndex === next.audioStreamIndex &&
      prev.subtitleStreamIndex === next.subtitleStreamIndex
    ) {
      return;
    }
    this.cache.set(itemId, next);
    this.emit();
  }

  /**
   * A beat of this device's own playback, mirrored the server's way (Plex's
   * rules, measured 2026-10-23): a position under a minute is not kept — a
   * short peek leaves no trace, and a started-over title keeps its old
   * resume point — and a crossing folds into "watched, no position" at
   * once, so the card trades its bar for the check without waiting for the
   * next fetch. The 90 % here is the transient guess at the server's played
   * threshold (a credits marker can bring the real crossing earlier); the
   * next ingest reconciles.
   */
  report(
    itemId: string,
    state: "playing" | "paused" | "stopped" | "ended",
    timeMs: number,
    durationMs: number,
  ): void {
    const prev = this.get(itemId);
    const watchedNow = durationMs > 0 && timeMs / durationMs >= 0.9;
    const ended = state === "ended";
    const crossed = watchedNow || ended;
    if (!crossed && timeMs < 60_000) return;
    const offset = crossed ? 0 : timeMs;
    // Spread, don't rebuild: a progress beat must never wipe fields it
    // doesn't own (the stream selection, notably). Recency moves with the
    // position or a crossing, never on a paused keep-alive at the same
    // spot (the server's last_viewed_at rule; Home's row order reads it).
    this.cache.set(itemId, {
      ...prev,
      watched: prev.watched || crossed,
      viewOffsetMs: offset,
      viewCount: prev.viewCount + (crossed && !prev.watched ? 1 : 0),
      lastPlayedAt: crossed || offset !== prev.viewOffsetMs ? Date.now() : prev.lastPlayedAt,
    });
    this.emit();
  }

  /**
   * Progress beat echoed from the events feed — another device of the same
   * account, or the server's expiry sweep. The event carries no durationMs,
   * so update offset/recency only and never derive watched/viewCount from
   * it; value-equal beats (this device's own echoes) drop without emitting.
   * The server echoes only what it stored (0 for a clear); a position under
   * a minute can still arrive from an older server and is not a resume
   * point, so it is dropped here too.
   */
  remoteProgress(itemId: string, timeMs: number): void {
    const prev = this.get(itemId);
    if (prev.viewOffsetMs === timeMs || (timeMs > 0 && timeMs < 60_000)) return;
    this.cache.set(itemId, { ...prev, viewOffsetMs: timeMs, lastPlayedAt: Date.now() });
    this.emit();
  }

  /**
   * A mark either way clears the position, as the server's does; a mark
   * watched is a view (recency now), an unwatch erases the viewing.
   */
  setWatched(itemId: string, watched: boolean): void {
    const prev = this.get(itemId);
    this.cache.set(itemId, {
      ...prev,
      watched,
      viewOffsetMs: 0,
      viewCount: watched ? prev.viewCount + 1 : 0,
      lastPlayedAt: watched ? Date.now() : null,
    });
    this.emit();
  }

  /** Optimistic local stream selection; shields it until the PUT settles. */
  setStreamSelection(
    itemId: string,
    sel: Partial<Pick<UserItemState, "audioStreamIndex" | "subtitleStreamIndex">>,
  ): void {
    this.dirtySelection.add(itemId);
    this.cache.set(itemId, { ...this.get(itemId), ...sel });
    this.emit();
  }

  /** The selection write settled (either way) — server truth may flow again. */
  clearSelectionDirty(itemId: string): void {
    this.dirtySelection.delete(itemId);
  }
}
